import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import initialData from "./initial-data";
import Column from "./Column";
import "./App.css";
import { fetchData } from "../network/apiService";

const SupportSummary = ({ consumerId, profileData }) => {
  const [data, setData] = useState(initialData);
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch Issues
  useEffect(() => {
    const fetchData1 = async () => {
      try {
        const response = await fetchData(consumerId);
        setIssues(response?.data?.issues || []);
      } catch (error) {
        console.error(error);
        setIssues([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData1();
  }, [consumerId]);

  // Extract description safely
  const getDescription = (document) => {
    const jsonString = document?.content?.[0]?.content?.[0]?.text || "";

    try {
      const parsed = JSON.parse(jsonString);
      return parsed?.description || jsonString;
    } catch {
      return jsonString;
    }
  };

  // Filter issues by userId (customfield_10045)
  const issuesArray = useMemo(() => {
    return issues
      .filter(
        (item) =>
          item?.fields?.customfield_10045 === profileData?.userId
      )
      .map((item) => ({
        id: item.key,
        key: item.key,
        label: item.fields.labels,
        currentStatus: item.fields.status.name,
        issueType: item.fields.issuetype.name,
        created: item.fields.created,
        summary: item.fields.summary,
        description: getDescription(item.fields.description)
      }));
  }, [issues, profileData]);

  const knownReviewStatuses = ["Waiting for support", "Review"];
  const knownResolvedStatuses = ["Canceled", "Closed", "Resolved", "Done"];

  const keysInReview = issuesArray
    .filter((issue) => knownReviewStatuses.includes(issue.currentStatus))
    .map((issue) => issue.key);

  const keysInResolved = issuesArray
    .filter((issue) => knownResolvedStatuses.includes(issue.currentStatus))
    .map((issue) => issue.key);

  const keysInProgress = issuesArray
    .filter(
      (issue) =>
        !knownReviewStatuses.includes(issue.currentStatus) &&
        !knownResolvedStatuses.includes(issue.currentStatus)
    )
    .map((issue) => issue.key);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const start = data.columns[source.droppableId];
    const finish = data.columns[destination.droppableId];

    if (start === finish) {
      const newTaskIds = Array.from(start.taskIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);

      const newColumn = { ...start, taskIds: newTaskIds };

      setData({
        ...data,
        columns: {
          ...data.columns,
          [newColumn.id]: newColumn
        }
      });

      return;
    }

    const startTaskIds = Array.from(start.taskIds);
    startTaskIds.splice(source.index, 1);

    const finishTaskIds = Array.from(finish.taskIds);
    finishTaskIds.splice(destination.index, 0, draggableId);

    setData({
      ...data,
      columns: {
        ...data.columns,
        [start.id]: { ...start, taskIds: startTaskIds },
        [finish.id]: { ...finish, taskIds: finishTaskIds }
      }
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div style={{ borderTopLeftRadius: 10, border: "1px solid #e1e1e1" }}>
      <div
        style={{
          borderTopLeftRadius: 10,
          background: "linear-gradient(to left, #156ab3, #0f4c81 -15%)",
          height: 60,
          padding: 10
        }}
      >
        <img
          src="https://cdn.roemanu.io/los/mmtc/emails/mmtc-logo.jpg"
          alt="logo"
          style={{ height: 39, marginLeft: 10 }}
        />
      </div>

      <h1
        style={{
          fontSize: 20,
          padding: 20,
          fontWeight: 500,
          textAlign: "left"
        }}
      >
        Ticket Tracker
      </h1>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="App">
          {data.columnOrder.map((columnId) => {
            const column = data.columns[columnId];

            let taskIds = [];
            if (column.title === "Submitted") {
              taskIds = keysInReview;
            } else if (column.title === "In Progress") {
              taskIds = keysInProgress;
            } else if (column.title === "Resolved") {
              taskIds = keysInResolved;
            }

            const tasks = issuesArray.filter((task) =>
              taskIds.includes(task.id)
            );

            const columnWithCount = {
              ...column,
              taskIds,
              title: `${column.title} (${taskIds.length})`
            };

            return (
              <Column
                key={column.id}
                column={columnWithCount}
                tasks={tasks}
              />
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
};

export { SupportSummary };
export default SupportSummary;